<script setup lang="ts">
import { useDisplay } from 'vuetify'
import CartGodmodeFlow from '@/components/cart/Cart2GodmodeFlow.vue'
import CartPaymentSummary from '@/components/cart/Cart2PaymentSummary.vue'
import CartPersonalInfo from '@/components/cart/Cart2PersonalInfo.vue'
import { useCartStore } from '@/stores/cart'
import { useGodmodeSlots } from '~/composables/useGodmodeSlots'

definePageMeta({
  layout: 'front-page',
  public: true,
  pageTitle: {
    title: 'Je winkelmandje',
    subtitle: 'Voltooi je bestelling',
    icon: 'bx-cart',
    iconColor: 'success',
    compact: true,
  },
})

const { smAndDown, mdAndUp } = useDisplay()
const route = useRoute()
const authStore = useAuthStore()
const cartStore = useCartStore()
const { slots, createSlot, resetSlots } = useGodmodeSlots()

// Loading state for initial page load
const pageReady = ref(false)

// Desktop step: 1 = tickets overview + auth, 2 = attendee assignment + payment
// Mobile step: 1 = tickets overview, 2 = auth, 3 = attendee assignment, 4 = payment
const desktopStep = ref<1 | 2>(1)
const mobileStep = ref<1 | 2 | 3 | 4>(1)

// Loading state for camp forms
const loadingForms = ref(true)
const formError = ref<string | null>(null)

// Reference to payment summary component for triggering payment
const paymentSummaryRef = ref<{ completePayment: () => void, isSubmitting: boolean, canPay: boolean, isPaymentActive: boolean } | null>(null)

// Whether Stripe payment element is active (disables attendee editing)
const isPaymentActive = computed(() => paymentSummaryRef.value?.isPaymentActive ?? false)

// Reference to personal info component for saving profile data
const personalInfoRef = ref<{ saveProfileData: () => Promise<void>, isAddressComplete: boolean } | null>(null)

// Whether address is complete (for gating navigation)
const isAddressComplete = computed(() => personalInfoRef.value?.isAddressComplete ?? false)

// True when the cart was loaded from a booking invite link (capacity bypass active)
const isInviteMode = computed(() => !!cartStore.invite_token)

// True when invite mode but the user may pick their own tickets (phase 1 is NOT disabled)
const isInviteFreeChoice = computed(() => !!cartStore.invite_token && cartStore.invite_free_choice)

// True when invite mode with pre-configured slots (phase 1 IS disabled)
const isInvitePreConfigured = computed(() => !!cartStore.invite_token && !cartStore.invite_free_choice)

// Invite token from URL (used for hydration + preserved through login redirects)
const inviteToken = computed(() => {
  const q = route.query.invite

  return typeof q === 'string' && q.length === 64 ? q : null
})

// ── Pre-set invite_token in setup (BEFORE any child mounts) ──────────────────
// Cart2GodmodeFlow.onMounted reads props.ignoreAvailability = isInviteMode to
// decide whether to run fixSoldOutSlots. If invite_token is only set in
// onMounted (async), the child mounts first and converts week → day slots.
// Setting it here synchronously ensures the child always sees ignoreAvailability=true.
if (inviteToken.value && !cartStore.invite_token)
  cartStore.invite_token = inviteToken.value

const magicLinkRedirect = computed(() => {
  const mode = smAndDown.value ? 'mobile' : 'desktop'
  const step = smAndDown.value ? mobileStep.value : desktopStep.value
  const inviteParam = inviteToken.value ? `&invite=${inviteToken.value}` : ''

  return `/cart?mode=${mode}&step=${step}${inviteParam}`
})

// Computed: Get camp forms
const campForms = computed(() => cartStore.camp_forms)

// Go back to camp
const goToCamp = () => {
  if (cartStore.camp_id) {
    navigateTo(`/camp-details?id=${cartStore.camp_id}`)
  }
  else {
    navigateTo('/')
  }
}

// Load camp forms
const loadCampForms = async () => {
  loadingForms.value = true
  formError.value = null

  try {
    if (cartStore.camp_id && !cartStore.camp_forms_loaded)
      await cartStore.loadCampForms()
  }
  catch {
    formError.value = 'Fout bij laden formulieren. Probeer opnieuw.'
  }
  finally {
    loadingForms.value = false
  }
}

// Check if all tickets are complete (including form fields)
const allTicketsComplete = computed(() => {
  return cartStore.canProceedToPayment && cartStore.allFormsComplete
})

// Step navigation helpers
const canProceedToStep2 = computed(() => {
  if (cartStore.isEmpty)
    return false
  for (const item of cartStore.items) {
    if (item.ticket_type === 'day' && (!item.day_tickets || item.day_tickets.length === 0))
      return false
  }

  // Block if any day-type slot has no days selected yet (not yet reflected in cart items)
  for (const slot of slots.value) {
    if (slot.type === 'day' && slot.selectedDays.length === 0)
      return false
  }

  return true
})

const canProceedToAttendeeStep = computed(() => {
  return authStore.isAuthenticated && !cartStore.isEmpty && isAddressComplete.value
})

const canProceedToPaymentStep = computed(() => {
  return allTicketsComplete.value && authStore.isAuthenticated
})

// Desktop navigation
const goToDesktopStep2 = async () => {
  if (!authStore.isAuthenticated) {
    return
  }

  if (!canProceedToStep2.value) {
    return
  }

  if (!isAddressComplete.value) {
    return
  }

  // Save profile data before proceeding
  await personalInfoRef.value?.saveProfileData()
  desktopStep.value = 2
}

const goToDesktopStep1 = () => {
  desktopStep.value = 1
}

// Mobile navigation
const goToNextMobileStep = async () => {
  if (mobileStep.value === 1 && canProceedToStep2.value) {
    mobileStep.value = 2
  }
  else if (mobileStep.value === 2 && canProceedToAttendeeStep.value) {
    // Save profile data before proceeding to attendees
    await personalInfoRef.value?.saveProfileData()
    mobileStep.value = 3
  }
  else if (mobileStep.value === 3 && canProceedToPaymentStep.value) {
    mobileStep.value = 4
  }
}

const goToPreviousMobileStep = () => {
  if (mobileStep.value === 4) {
    mobileStep.value = 3
  }
  else if (mobileStep.value === 3) {
    mobileStep.value = 2
  }
  else if (mobileStep.value === 2) {
    mobileStep.value = 1
  }
  else {
    goToCamp()
  }
}

// Get mobile step titles
const mobileStepTitle = computed(() => {
  switch (mobileStep.value) {
    case 1:
      return cartStore.camp_name || 'Tickets'
    case 2:
      return 'Jouw gegevens'
    case 3:
      return 'Deelnemers'
    case 4:
      return 'Betaling'
    default:
      return 'Winkelwagen'
  }
})

// Get mobile footer button text
const mobileFooterButtonText = computed(() => {
  switch (mobileStep.value) {
    case 1:
      return 'Ga verder'
    case 2:
      return authStore.isAuthenticated ? 'Deelnemers' : 'Log eerst in'
    case 3:
      return 'Betaling'
    case 4:
      return 'Nu betalen'
    default:
      return 'Volgende'
  }
})

// Get mobile back button text
const mobileBackButtonText = computed(() => {
  switch (mobileStep.value) {
    case 4:
      return 'Deelnemers'
    case 3:
      return 'Gegevens'
    case 2:
      return 'Tickets'
    default:
      return 'Terug'
  }
})

// Missing requirements for mobile step 3 feedback (mirrors Cart2PaymentSummary logic, participants only)
const step3MissingRequirements = computed(() => {
  const missing: string[] = []

  const seenRrns = new Set<string>()

  cartStore.participants.forEach((p, i) => {
    const rrn = String(p.rijksregisternummer || '').replace(/\D/g, '')
    if (rrn.length === 11 && seenRrns.has(rrn))
      return
    if (rrn.length === 11)
      seenRrns.add(rrn)

    const label = p.first_name ? p.first_name : `Deelnemer ${i + 1}`

    if (!p.first_name && !p.last_name && !p.rijksregisternummer) {
      missing.push(`Er is nog geen deelnemer gekoppeld aan deelnemer ${i + 1}.`)

      return
    }
    if (!p.first_name || !p.last_name) {
      missing.push(`${label} mist nog een naam.`)

      return
    }
    if (rrn.length !== 11) {
      missing.push(`${label} mist nog een geldig rijksregisternummer.`)

      return
    }
    if (!p.date_of_birth) {
      missing.push(`${label} heeft een ongeldig rijksregisternummer.`)

      return
    }

    const formIssues = cartStore.missingFormFieldsPerParticipant.find(m => m.participantIndex === i)
    if (formIssues && formIssues.missingFields.length > 0) {
      const fields = formIssues.missingFields
      if (fields.length === 1)
        missing.push(`${label} mist nog "${fields[0]}".`)
      else
        missing.push(`${label} mist nog: ${fields.join(', ')}.`)
    }
  })

  return missing
})

// Is mobile footer button disabled
const isMobileFooterButtonDisabled = computed(() => {
  if (mobileStep.value === 1 && !canProceedToStep2.value)
    return true
  if (mobileStep.value === 2 && !authStore.isAuthenticated)
    return true
  if (mobileStep.value === 2 && !isAddressComplete.value)
    return true
  if (mobileStep.value === 4)
    return !paymentSummaryRef.value?.canPay

  return false
})

// Handle mobile footer action
const handleMobileFooterAction = async () => {
  if (mobileStep.value === 4) {
    paymentSummaryRef.value?.completePayment()
  }
  else {
    await goToNextMobileStep()
  }
}

// Handle login success - auto advance on mobile
const handleLoginSuccess = () => {
  if (smAndDown.value && mobileStep.value === 2) {
    setTimeout(() => {
      mobileStep.value = 3
    }, 500)
  }
}

const applyStepFromQuery = () => {
  const modeParam = Array.isArray(route.query.mode) ? route.query.mode[0] : route.query.mode
  const stepParam = Array.isArray(route.query.step) ? route.query.step[0] : route.query.step
  const parsedStep = typeof stepParam === 'string' ? Number(stepParam) : Number(stepParam ?? Number.NaN)

  // Invite mode: if the cart has a pre-configured invite token,
  // skip step 1 — but only if it's NOT free choice.
  // Free choice invites still show phase 1 so the user can pick their tickets.
  if (cartStore.invite_token && !cartStore.invite_free_choice) {
    if (authStore.isAuthenticated) {
      // Already logged in: skip to attendees step
      desktopStep.value = 2
      mobileStep.value = 3
    }
    else {
      // Need login first
      desktopStep.value = 1
      mobileStep.value = 2
    }

    return
  }

  if (Number.isNaN(parsedStep))
    return

  if (modeParam === 'mobile' || (!modeParam && smAndDown.value)) {
    if (parsedStep >= 1 && parsedStep <= 4)
      mobileStep.value = parsedStep as 1 | 2 | 3 | 4
  }

  if (modeParam === 'desktop' || (!modeParam && mdAndUp.value)) {
    if (parsedStep >= 1 && parsedStep <= 2)
      desktopStep.value = parsedStep as 1 | 2
  }
}

// Load cart from localStorage on mount
const hydrateFromInvite = async (token: string) => {
  try {
    const response = await $fetch<{
      success: boolean
      is_valid: boolean
      reason?: string
      data?: {
        invite_id: string
        token: string
        label: string | null
        slots_data: Array<{ type: 'week' | 'day', selectedDays: string[] }>
        allow_free_choice: boolean
        expires_at: string | null
        camp: {
          id: string
          name: string
          location: string | null
          start_date: string | null
          end_date: string | null
          refund_policy: string | null
          enable_invoice: boolean | null
          payment_methods: string[] | null
        }
      }
    }>(`/api/booking-invites/${token}`)

    if (!response.is_valid || !response.data)
      return false

    const { camp, allow_free_choice } = response.data

    cartStore.$patch(state => {
      state.camp_id = camp.id
      state.camp_name = camp.name
      state.camp_location = camp.location
      state.camp_start_date = camp.start_date
      state.camp_end_date = camp.end_date
      state.camp_refund_policy = camp.refund_policy
      state.camp_enable_invoice = camp.enable_invoice
      state.camp_payment_methods = camp.payment_methods
      state.invite_token = response.data!.token
      state.invite_free_choice = allow_free_choice
      state.items = []
      state.participants = []
    })

    resetSlots()
    if (!allow_free_choice) {
      // Pre-configured: restore the admin's slot setup
      slots.value = response.data.slots_data.map((s, i) => {
        const slot = createSlot(i === 0)

        slot.type = s.type
        slot.selectedDays = [...(s.selectedDays ?? [])]

        return slot
      })
    }

    cartStore.saveToLocalStorage()

    // Store the invite token in both sessionStorage and localStorage so it
    // redirects; localStorage covers Google/Microsoft OAuth which may clear
    // sessionStorage during cross-site navigation).
    // We store a timestamped object so stale tokens are not replayed.
    if (import.meta.client) {
      sessionStorage.setItem('invite_token', token)
      localStorage.setItem('invite_token', JSON.stringify({ token, ts: Date.now() }))
    }

    return true
  }
  catch {
    return false
  }
}

onMounted(async () => {
  cartStore.loadFromLocalStorage()

  // Resolve the invite token: URL param takes priority, then sessionStorage,
  // then localStorage (survives Google OAuth cross-site redirect).
  // localStorage entries expire after 2 hours to avoid replaying stale tokens.
  const getStoredInviteToken = (): string | null => {
    if (!import.meta.client)
      return null

    const fromSession = sessionStorage.getItem('invite_token')
    if (fromSession)
      return fromSession

    try {
      const raw = localStorage.getItem('invite_token')
      if (raw) {
        const { token, ts } = JSON.parse(raw) as { token: string, ts: number }
        const twoHours = 2 * 60 * 60 * 1000
        if (Date.now() - ts < twoHours)
          return token

        // Expired — clean up
        localStorage.removeItem('invite_token')
      }
    }
    catch {
      localStorage.removeItem('invite_token')
    }

    return null
  }

  const tokenToHydrate = inviteToken.value || getStoredInviteToken()

  if (tokenToHydrate) {
    const ok = await hydrateFromInvite(tokenToHydrate)
    if (!ok) {
      // Invalid/used invite — clear token and both storages
      cartStore.invite_token = null
      if (import.meta.client) {
        sessionStorage.removeItem('invite_token')
        localStorage.removeItem('invite_token')
      }
    }
  }

  await loadCampForms()

  // Recalculate total to fetch discount rules for this camp
  if (cartStore.camp_id && cartStore.items.length > 0)
    await cartStore.recalculateTotal()

  applyStepFromQuery()

  // Mark page as ready after initial load
  pageReady.value = true
})

// Watch cart changes and save to localStorage
watch(
  () => cartStore.$state,
  () => {
    cartStore.saveToLocalStorage()
  },
  { deep: true },
)
</script>

<template>
  <div
    class="cart-page"
    :class="{ 'mobile-fullscreen': smAndDown }"
  >
    <!-- Loading State (client-only to avoid VProgressCircular hydration mismatch) -->
    <ClientOnly>
      <div v-if="!pageReady" class="page-loading-state">
        <VContainer class="d-flex align-center justify-center" style="min-block-size: 50vh">
          <div class="text-center">
            <VProgressCircular
              indeterminate
              color="primary"
              size="48"
              class="mb-4"
            />
            <p class="text-body-2 text-medium-emphasis">
              Laden...
            </p>
          </div>
        </VContainer>
      </div>
      <template #fallback>
        <div class="page-loading-state">
          <VContainer class="d-flex align-center justify-center" style="min-block-size: 50vh">
            <div class="text-center">
              <VSkeletonLoader type="avatar" class="mx-auto mb-4" />
              <p class="text-body-2 text-medium-emphasis">
                Laden...
              </p>
            </div>
          </VContainer>
        </div>
      </template>
    </ClientOnly>

    <!-- Mobile Header -->
    <VAppBar
      v-if="pageReady && smAndDown"
      flat
      color="surface"
      class="mobile-checkout-header"
    >
      <VBtn
        icon
        variant="text"
        @click="goToPreviousMobileStep"
      >
        <VIcon icon="bx-arrow-back" />
      </VBtn>
      <VAppBarTitle class="mobile-header-title">
        <h3>{{ mobileStepTitle }}</h3>
      </VAppBarTitle>

      <VBtn
        icon
        variant="text"
        @click="goToCamp"
      >
        <VIcon icon="bx-x" />
      </VBtn>
    </VAppBar>

    <!-- Mobile Step Indicator -->
    <div v-if="pageReady && smAndDown && !cartStore.isEmpty" class="mobile-step-indicator">
      <div class="step-dots">
        <div
          v-for="step in 4"
          :key="step"
          class="step-dot"
          :class="{
            active: mobileStep === step,
            completed: mobileStep > step,
          }"
        />
      </div>
      <div class="step-label text-caption text-medium-emphasis">
        Stap {{ mobileStep }} van 4
      </div>
    </div>

    <VContainer v-if="pageReady" :class="{ 'mobile-container': smAndDown }">
      <div class="checkout-card">
        <VCard :rounded="smAndDown ? 0 : 'lg'">
          <VCardText :class="smAndDown ? 'mobile-card-content' : ''">
            <!-- DESKTOP: Step 1 - Tickets Overview + Auth -->
            <VRow v-if="mdAndUp && desktopStep === 1" class="flex-column flex-md-row">
              <!-- Left Column: Tickets Overview -->
              <VCol cols="12" md="6" class="border-e">
                <div class="pa-4">
                  <!-- Header -->
                  <div class="d-flex align-center justify-space-between gap-3 mb-6">
                    <div class="d-flex align-center gap-3">
                      <VBtn
                        icon
                        variant="text"
                        size="small"
                        @click="goToCamp"
                      >
                        <VIcon icon="bx-arrow-back" />
                      </VBtn>
                      <VAvatar color="primary" variant="tonal" size="32">
                        <span class="text-body-1 font-weight-bold">1</span>
                      </VAvatar>
                      <div>
                        <h4 class="text-h5 mb-0 font-weight-bold">
                          <template v-if="!cartStore.isEmpty && cartStore.camp_name">
                            {{ cartStore.camp_name }}<span v-if="cartStore.camp_period" class="text-medium-emphasis"> &mdash; {{ cartStore.camp_period }}</span>
                          </template>
                          <template v-else>
                            Mijn Tickets
                          </template>
                        </h4>
                        <div v-if="cartStore.isEmpty && cartStore.camp_name" class="text-body-2 text-medium-emphasis">
                          {{ cartStore.camp_name }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Empty Cart State (no camp selected) -->
                  <VCard v-if="!cartStore.camp_id" variant="outlined" class="mb-6">
                    <VCardText class="text-center py-16">
                      <VIcon icon="bx-cart" size="80" color="disabled" class="mb-4" />
                      <h5 class="text-h5 mb-2">
                        Je winkelwagen is leeg
                      </h5>
                      <p class="text-body-2 text-medium-emphasis mb-6">
                        Voeg tickets toe om verder te gaan met je boeking
                      </p>
                      <VBtn color="primary" @click="navigateTo('/')">
                        <VIcon icon="bx-home" start />
                        Terug naar home
                      </VBtn>
                    </VCardText>
                  </VCard>

                  <!-- Ticket type selection (godmode flow for all users) -->
                  <template v-else>
                    <!-- Pre-configured invite: read-only, phase 1 skipped -->
                    <VAlert
                      v-if="isInvitePreConfigured"
                      type="info"
                      variant="tonal"
                      class="mb-4"
                      closable
                    >
                      <template #prepend>
                        <VIcon icon="bx-link" />
                      </template>
                      <div class="text-body-2 font-weight-medium">
                        Gereserveerde toegangslink
                      </div>
                      <div class="text-caption text-medium-emphasis">
                        De tickets voor deze boeking zijn reeds ingesteld door de beheerder. Ga verder naar de volgende stap om de deelnemers in te vullen.
                      </div>
                    </VAlert>
                    <!-- Free-choice invite: user picks their own tickets -->
                    <VAlert
                      v-else-if="isInviteFreeChoice"
                      type="info"
                      variant="tonal"
                      class="mb-4"
                      closable
                    >
                      <template #prepend>
                        <VIcon icon="bx-link" />
                      </template>
                      <div class="text-body-2 font-weight-medium">
                        Gereserveerde toegangslink
                      </div>
                      <div class="text-caption text-medium-emphasis">
                        Kies hieronder je tickets voor dit kamp. Je boeking wordt gereserveerd zonder rekening te houden met de beschikbaarheid.
                      </div>
                    </VAlert>
                    <CartGodmodeFlow
                      :phase="1"
                      :camp-forms="campForms"
                      :disabled="isInvitePreConfigured"
                      :ignore-availability="isInviteMode"
                    />
                  </template>
                </div>
              </VCol>

              <!-- Right Column: Personal Info / Auth -->
              <VCol cols="12" md="6">
                <div class="pa-4">
                  <div class="sticky-sidebar">
                    <!-- Step 2 Header -->
                    <div class="d-flex align-center gap-3 mb-4">
                      <VAvatar color="primary" variant="tonal" size="32">
                        <span class="text-body-1 font-weight-bold">2</span>
                      </VAvatar>
                      <h4 class="text-h5 mb-0 font-weight-bold">
                        Mijn Informatie
                      </h4>
                    </div>
                    <CartPersonalInfo
                      ref="personalInfoRef"
                      :magic-link-redirect="magicLinkRedirect"
                      @login-success="handleLoginSuccess"
                    />
                  </div>
                </div>
              </VCol>
            </VRow>

            <!-- Desktop Step 1 Footer -->
            <div v-if="mdAndUp && desktopStep === 1 && !cartStore.isEmpty" class="desktop-step-footer pa-4 border-t">
              <div class="d-flex justify-end align-center gap-4">
                <VAlert
                  v-if="!authStore.isAuthenticated"
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="flex-grow-1 mb-0"
                >
                  <template #prepend>
                    <VIcon icon="bx-info-circle" />
                  </template>
                  <div class="text-body-2">
                    Log in of maak een account aan om je boeking te voltooien.
                  </div>
                </VAlert>
                <VBtn
                  v-if="authStore.isAuthenticated"
                  color="success"
                  size="large"
                  :disabled="!isAddressComplete || !canProceedToStep2"
                  @click="goToDesktopStep2"
                >
                  Naar deelnemers
                  <VIcon icon="bx-right-arrow-alt" end />
                </VBtn>
              </div>
            </div>

            <!-- DESKTOP: Step 2 - Attendee Assignment + Payment -->
            <VRow v-if="mdAndUp && desktopStep === 2" class="flex-column flex-md-row">
              <!-- Left Column: Ticket Cards with Attendee Assignment -->
              <VCol cols="12" md="8" class="border-e">
                <div class="pa-4">
                  <div class="d-flex align-center justify-space-between gap-3 mb-6">
                    <div class="d-flex align-center gap-3">
                      <VBtn
                        icon
                        variant="text"
                        size="small"
                        @click="goToDesktopStep1"
                      >
                        <VIcon icon="bx-arrow-back" />
                      </VBtn>
                      <VAvatar color="primary" variant="tonal" size="32">
                        <span class="text-body-1 font-weight-bold">3</span>
                      </VAvatar>
                      <h4 class="text-h5 mb-0 font-weight-bold">
                        Deelnemers
                      </h4>
                    </div>
                  </div>

                  <VCard v-if="loadingForms" variant="outlined" class="pa-6 mb-6">
                    <div class="d-flex flex-column align-center">
                      <VProgressCircular indeterminate color="primary" size="40" class="mb-3" />
                      <span class="text-caption">Formulieren laden...</span>
                    </div>
                  </VCard>

                  <VAlert v-else-if="formError" type="error" variant="tonal" density="compact" class="mb-6">
                    <div class="text-caption">
                      {{ formError }}
                    </div>
                    <template #append>
                      <VBtn variant="text" color="error" size="x-small" @click="loadCampForms">
                        Opnieuw
                      </VBtn>
                    </template>
                  </VAlert>

                  <div v-if="!loadingForms && !formError" class="d-flex flex-column gap-3">
                    <CartGodmodeFlow
                      :camp-forms="campForms"
                      :disabled="isPaymentActive"
                      :ignore-availability="isInviteMode"
                    />
                  </div>
                </div>
              </VCol>

              <!-- Right Column: Payment Summary -->
              <VCol cols="12" md="4" class="right-column-wrapper">
                <div class="pa-4">
                  <div class="sticky-sidebar">
                    <!-- Step 4 Header -->
                    <div class="d-flex align-center gap-3 mb-4">
                      <VAvatar color="primary" variant="tonal" size="32">
                        <span class="text-body-1 font-weight-bold">4</span>
                      </VAvatar>
                      <h4 class="text-h5 mb-0 font-weight-bold">
                        Betaling
                      </h4>
                    </div>
                    <CartPaymentSummary
                      ref="paymentSummaryRef"
                      :disabled="!authStore.isAuthenticated"
                    />
                  </div>
                </div>
              </VCol>
            </VRow>

            <!-- MOBILE: Step 1 - Tickets Overview -->
            <div v-if="smAndDown && mobileStep === 1">
              <div class="pa-2">
                <VCard v-if="!cartStore.camp_id" variant="outlined" class="mb-6">
                  <VCardText class="text-center py-16">
                    <VIcon icon="bx-cart" size="80" color="disabled" class="mb-4" />
                    <h5 class="text-h5 mb-2">
                      Je winkelwagen is leeg
                    </h5>
                    <p class="text-body-2 text-medium-emphasis mb-6">
                      Voeg tickets toe om verder te gaan met je boeking
                    </p>
                    <VBtn color="primary" @click="navigateTo('/')">
                      <VIcon icon="bx-home" start />
                      Terug naar home
                    </VBtn>
                  </VCardText>
                </VCard>

                <!-- Ticket type selection (godmode flow for all users) -->
                <template v-else>
                  <!-- Pre-configured invite -->
                  <VAlert
                    v-if="isInvitePreConfigured"
                    type="info"
                    variant="tonal"
                    class="mb-4"
                    closable
                  >
                    <template #prepend>
                      <VIcon icon="bx-link" />
                    </template>
                    <div class="text-body-2 font-weight-medium">
                      Gereserveerde toegangslink
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      De tickets zijn reeds ingesteld door de beheerder. Ga verder om de deelnemers in te vullen.
                    </div>
                  </VAlert>
                  <!-- Free-choice invite -->
                  <VAlert
                    v-else-if="isInviteFreeChoice"
                    type="info"
                    variant="tonal"
                    class="mb-4"
                    closable
                  >
                    <template #prepend>
                      <VIcon icon="bx-link" />
                    </template>
                    <div class="text-body-2 font-weight-medium">
                      Gereserveerde toegangslink
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      Kies je tickets voor dit kamp. Je boeking wordt gereserveerd.
                    </div>
                  </VAlert>
                  <CartGodmodeFlow
                    :phase="1"
                    :camp-forms="campForms"
                    :disabled="isInvitePreConfigured"
                    :ignore-availability="isInviteMode"
                  />
                </template>
              </div>
            </div>

            <!-- MOBILE: Step 2 - Personal Info / Auth -->
            <div v-if="smAndDown && mobileStep === 2">
              <div class="pa-2">
                <CartPersonalInfo
                  ref="personalInfoRef"
                  :magic-link-redirect="magicLinkRedirect"
                  @login-success="handleLoginSuccess"
                />
              </div>
            </div>

            <!-- MOBILE: Step 3 - Attendee Assignment -->
            <div v-if="smAndDown && mobileStep === 3">
              <div class="pa-2">
                <VCard v-if="loadingForms" variant="outlined" class="pa-6 mb-6">
                  <div class="d-flex flex-column align-center">
                    <VProgressCircular indeterminate color="primary" size="40" class="mb-3" />
                    <span class="text-caption">Formulieren laden...</span>
                  </div>
                </VCard>

                <VAlert v-else-if="formError" type="error" variant="tonal" density="compact" class="mb-4">
                  <div class="text-caption">
                    {{ formError }}
                  </div>
                  <template #append>
                    <VBtn variant="text" color="error" size="x-small" @click="loadCampForms">
                      Opnieuw
                    </VBtn>
                  </template>
                </VAlert>

                <div v-if="!loadingForms && !formError" class="d-flex flex-column gap-3">
                  <CartGodmodeFlow
                    :camp-forms="campForms"
                    :disabled="isPaymentActive"
                    :ignore-availability="isInviteMode"
                  />

                  <VAlert
                    v-if="step3MissingRequirements.length > 0"
                    type="warning"
                    variant="tonal"
                    density="compact"
                  >
                    <div class="d-flex flex-column gap-1">
                      <div v-for="msg in step3MissingRequirements" :key="msg" class="text-body-2">
                        {{ msg }}
                      </div>
                    </div>
                  </VAlert>
                </div>
              </div>
            </div>

            <!-- MOBILE: Step 4 - Payment -->
            <div v-if="smAndDown && mobileStep === 4">
              <div class="pa-2">
                <div class="mb-2">
                  <h6 class="text-h6 font-weight-bold mb-1">
                    Betaling
                  </h6>
                  <p class="text-body-2 text-medium-emphasis mb-0">
                    Controleer en betaal je bestelling.
                  </p>
                </div>

                <CartPaymentSummary
                  ref="paymentSummaryRef"
                  :disabled="!authStore.isAuthenticated"
                />
              </div>
            </div>
          </VCardText>
        </VCard>
      </div>
    </VContainer>

    <!-- Mobile Sticky Footer -->
    <div
      v-if="pageReady && smAndDown && !cartStore.isEmpty"
      class="mobile-sticky-footer"
    >
      <VBtn
        color="secondary"
        variant="tonal"
        @click="goToPreviousMobileStep"
      >
        <VIcon icon="bx-chevron-left" start />
        {{ mobileBackButtonText }}
      </VBtn>

      <VBtn
        color="success"
        :disabled="isMobileFooterButtonDisabled"
        :loading="mobileStep === 4 && paymentSummaryRef?.isSubmitting"
        @click="handleMobileFooterAction"
      >
        {{ mobileFooterButtonText }}
        <VIcon :icon="mobileStep === 4 ? 'bx-credit-card' : 'bx-right-arrow-alt'" end />
      </VBtn>
    </div>
  </div>
</template>

<style lang="scss">
// Mobile fullscreen styles
.cart-page.mobile-fullscreen {
  position: fixed;
  z-index: 1000;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  inset: 0;

  .mobile-checkout-header {
    position: relative !important;
    z-index: 10;
    flex-shrink: 0;
    border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

    .mobile-header-title {
      font-family: "Peachy Keen JF", sans-serif !important;
      font-size: 1.25rem;
      font-weight: 500;
    }
  }

  .mobile-step-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgb(var(--v-theme-surface));
    border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    gap: 1rem;
    padding-block: 0.5rem;
    padding-inline: 1rem;

    .step-dots {
      display: flex;
      gap: 0.5rem;
    }

    .step-dot {
      border-radius: 50%;
      background: rgba(var(--v-border-color), 0.3);
      block-size: 8px;
      inline-size: 8px;
      transition: all 0.2s ease;

      &.active {
        border-radius: 4px;
        background: rgb(var(--v-theme-primary));
        inline-size: 24px;
      }

      &.completed {
        background: rgb(var(--v-theme-success));
      }
    }
  }

  .v-card-text {
    flex: 0 0 auto;
  }

  .mobile-card-content {
    flex: 1 1 auto !important;
  }

  .mobile-container {
    overflow: hidden auto;
    flex: 1;
    padding: 0 !important;
    max-inline-size: 100% !important;
  }

  .checkout-card {
    block-size: 100%;

    .v-card {
      display: flex;
      flex-direction: column;
      block-size: 100%;
      min-block-size: 100%;
    }

    .mobile-card-content {
      display: block;
      overflow: hidden auto;
      flex: 1;
      padding: 0.75rem !important;
      padding-block-end: calc(5rem + env(safe-area-inset-bottom, 0px)) !important;

      .v-row {
        display: flex;
        flex-flow: column nowrap;
        margin: 0 !important;

        > .v-col,
        > [class*="v-col-"] {
          flex: 0 0 auto;
          padding: 0 !important;
          max-inline-size: 100%;
        }
      }

      .text-h4 {
        font-size: 1.125rem !important;
        line-height: 1.4 !important;
        margin-block-end: 0.5rem !important;
      }

      .text-h5 {
        font-size: 1rem !important;
        line-height: 1.3 !important;
      }

      .text-h6 {
        font-size: 0.9rem !important;
        line-height: 1.3 !important;
      }

      .mb-6 {
        margin-block-end: 0.75rem !important;
      }

      .mb-4 {
        margin-block-end: 0.5rem !important;
      }

      .v-card .v-card-text {
        padding: 0.75rem !important;
      }

      .pa-4 {
        padding: 0.75rem !important;
      }

      .pa-3 {
        padding: 0.5rem !important;
      }

      .py-16 {
        padding-block: 2rem !important;
      }

      .v-divider {
        margin-block: 0.5rem !important;
      }

      .v-alert {
        padding: 0.75rem !important;
      }

      .border-e {
        border-inline-end: none !important;
      }
    }
  }
}

// Mobile sticky footer
.mobile-sticky-footer {
  position: fixed;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  background: rgb(var(--v-theme-surface));
  border-block-start: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  inset-block-end: 0;
  inset-inline: 0;
  padding-block: 0.75rem;
  padding-block-end: calc(0.75rem + env(safe-area-inset-bottom, 0px));
  padding-inline: 1rem;
}

// Desktop styles
.cart-page:not(.mobile-fullscreen) {
  background: rgb(var(--v-theme-background));
  min-block-size: 100vh;
  padding-block: 1rem;
}

.sticky-sidebar {
  position: sticky;
}

// Desktop stepper header
.stepper-header {
  .step-item {
    border-radius: 0.5rem;
    padding-block: 0.75rem;
    padding-inline: 1.5rem;
    transition: all 0.2s ease;

    &:hover:not(.disabled) {
      background: rgba(var(--v-theme-on-surface), 0.04);
    }

    &.active {
      background: rgba(var(--v-theme-primary), 0.08);
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}

// Desktop step footer
.desktop-step-footer {
  background: rgba(var(--v-theme-on-surface), 0.02);
}

// Ticket card styles
.ticket-card {
  overflow: hidden;
  border-radius: 0.5rem;
}

.ticket-day-chip {
  align-items: flex-start;
  block-size: auto !important;

  .v-chip__content {
    display: flex;
    align-items: flex-start;
    line-height: 1.2;
    white-space: normal;
  }
}

.ticket-accent-bar {
  flex-shrink: 0;
  inline-size: 4px;
}

.ticket-bg {
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.ticket-add-card {
  border-style: dashed;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
    background-color: rgba(var(--v-theme-primary), 0.04);
  }
}

// Responsive adjustments
@media (max-width: 960px) and (min-width: 600px) {
  .cart-page:not(.mobile-fullscreen) {
    .v-container {
      padding-inline: 2rem !important;
    }
  }
}

@media (max-width: 600px) {
  .cart-page:not(.mobile-fullscreen) {
    .v-container {
      padding-inline: 1rem !important;
    }

    .v-card {
      border-radius: 0.5rem;
    }
  }
}
</style>
